// Modules
import SuggestionResults from "components/shared/SearchBar/SuggestionResults";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {htmlToText} from "html-to-text";
import debounce from "lodash.debounce";
import {useRouter} from "next/router";
import {useCallback, useState} from "react";

// Redux
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {clearSuggestionsResults, searchBarValueSelector, setSearchBarValue} from "redux/searchPage/searchBar/searchBarSlice";
import {categoriesSelector} from "redux/searchPage/categories/categoriesSlice";
import {setSorting} from "redux/searchPage/showResults/showResultsSlice";

// Hooks
import useRouting from "hooks/useRouting";

// Osa Search
import {executeSuggestionsQuery} from "services/osaSearch/osaSearch";

// Types
import {ResultQuery} from "interfaces/searchPage/osaSearch";
import {
	ChangeEvent,
	RenderSuggestionParams,
	ShouldRenderReasons,
	SuggestionSelectedEventData,
	SuggestionsFetchRequestedParams
} from "react-autosuggest";

// Constants
import {QueryStringPrimaryKeys} from "constants/searchPage/urlEnum";
import {Sorting} from "constants/searchPage/gpoSearchEnum";

// Utils
import {escapeRegexCharacters, formatSearchTitleResult} from "utils/searchPage/textFormat";
import {
	appendQueryStringPrimaryKeysInUrl,
	createStringFromQueryObject,
	hasQuerySearch,
	removeSpecificKeyFromQueryString
} from "utils/searchPage/queryString";

const useSearchBar = () => {
	const dispatch = useAppDispatch();
	const router = useRouter();

	const searchBarValue = useAppSelector(searchBarValueSelector);
	const categories = useAppSelector(categoriesSelector);
	const [suggestions, setSuggestions] = useState<ResultQuery[]>([]);
	const pushNewRoute = useRouting();


	const queryStringHandler = useCallback(
		(value: string) => {
			value = value.replace(/ /g, '+');
			const newQueryString = appendQueryStringPrimaryKeysInUrl({
				routerQuery: router.query,
				queryString: {
					queryStringKey: QueryStringPrimaryKeys.SEARCH,
					queryStringValue: value
				},
				removePaginationFilter: true
			});

			const sorting = hasQuerySearch(value) ? Sorting.RELEVANCE : Sorting.DATE;
			dispatch(setSorting(sorting));
			const query = createStringFromQueryObject(newQueryString)
			const currentUrl = window.location.pathname;
			const urlPath = currentUrl.split(window.location.origin)[1] || currentUrl;
			if (urlPath.includes(`/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}`)) {
				pushNewRoute(query);
			} else {
				window.location.href = `${process.env.NEXT_PUBLIC_DOMAIN}/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}${query}`;
			}
		},
		[router]
	);

	const debouncedSearch = useCallback(
		debounce((searchValue: string) => {
			executeSuggestionsQuery(searchValue, categories).then((res) => {
				if (res) {
					setSuggestions(res.results);
				}
			});
		}, 500),
		[categories]
	);

	const getSuggestionValue = (suggestion: Partial<ResultQuery>): string => formatSearchTitleResult(suggestion.title as string) || "";

	const onSuggestionsFetchRequested = (suggestionsFetchRequested: SuggestionsFetchRequestedParams) => {
		const escapedValue = escapeRegexCharacters(suggestionsFetchRequested.value.trim());
		if (escapedValue === "") {
			dispatch(clearSuggestionsResults());
		} else {
			debouncedSearch(escapedValue);
		}
	};

	const onSuggestionsClearRequested = () => setSuggestions([]);

	const onChangeValue = (event: React.FormEvent<HTMLElement>, changeEvent: ChangeEvent) => {
		dispatch(setSearchBarValue(changeEvent.newValue));
	};

	const onKeyPress = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (searchBarValue.length > 0) {
				if (event.key === "Enter") {
					handleSubmitSearch();
				}
			}
		},
		[searchBarValue]
	);

	const clearValue = useCallback(() => {
		if (router.query && router.isReady) {
			if (Object.keys(router.query).includes(QueryStringPrimaryKeys.SEARCH)) {
				const newQueryString = removeSpecificKeyFromQueryString(router.query, QueryStringPrimaryKeys.SEARCH);
				pushNewRoute(newQueryString);
			}
			dispatch(setSearchBarValue(""));
			setSuggestions([]);
		}
	}, [router]);

	const shouldRenderSuggestions = (value: string, reason: ShouldRenderReasons): boolean => {
		switch (reason) {
			case "input-changed":
			case "suggestions-updated":
			case "render":
				return value.trim().length >= 2;
			default:
				return false;
		}
	};

	const onSuggestionSelected = (event: React.FormEvent<HTMLElement>, data: SuggestionSelectedEventData<Partial<ResultQuery>>) => {
		if (event.type === "keydown" || event.type === "click") {
			window.location.href = data.suggestion.urlProduct as string;
		}
	};

	const renderSuggestion = (suggestion: Partial<ResultQuery>, { query }: RenderSuggestionParams) => {
		const suggestionText = htmlToText(`${suggestion.title}`);
		const matches = match(suggestionText, query);
		const parts = parse(suggestionText, matches);
		return <SuggestionResults parts={parts} />;
	};

	const handleSubmitSearch = useCallback(() => {
		queryStringHandler(searchBarValue || "*");
		dispatch(setSearchBarValue(""));
		setSuggestions([]);
	}, [searchBarValue]);

	return {
		suggestions,
		value: searchBarValue,
		getSuggestionValue,
		onSuggestionsFetchRequested,
		onSuggestionsClearRequested,
		onChangeValue,
		onKeyPress,
		clearValue,
		shouldRenderSuggestions,
		onSuggestionSelected,
		renderSuggestion,
		handleSubmitSearch
	};
};

export default useSearchBar;
